import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * A select you can type into.
 *
 * A plain <select> stops being usable somewhere around thirty options: the
 * native dropdown scrolls, type-ahead only matches the first letters, and
 * picking a country out of two hundred and fifty means dragging a scrollbar.
 *
 * The search is why the list does not need trimming. "Show the top 125
 * countries" sounds like a kindness to the person and is not — they type three
 * letters either way, and the 126th country is one somebody actually has to
 * enter. Length costs nothing here; absence costs a wrong answer.
 */

export interface ComboOption {
  value: string;
  label: string;
  /** Shown dim after the label — a code, a SKU, whatever disambiguates. */
  hint?: string;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  emptyLabel = '— none —',
  allowEmpty = true,
  disabled = false,
  id,
}: {
  options: readonly ComboOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  emptyLabel?: string;
  allowEmpty?: boolean;
  disabled?: boolean;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 80);
    // Prefix before substring, so "ind" offers India before territories that
    // merely contain the letters.
    const starts: ComboOption[] = [];
    const contains: ComboOption[] = [];
    for (const o of options) {
      const l = o.label.toLowerCase();
      if (l.startsWith(q) || o.hint?.toLowerCase() === q) starts.push(o);
      else if (l.includes(q) || o.hint?.toLowerCase().includes(q)) contains.push(o);
    }
    return [...starts, ...contains].slice(0, 80);
  }, [options, query]);

  // Clamped rather than reset: the highlight must never point past the end of a
  // list that just shrank under it, which is how Enter picks nothing.
  useEffect(() => setActive((i) => Math.min(i, Math.max(0, matches.length - 1))), [matches.length]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const pick = (v: string | null) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => {
          setOpen((o) => !o);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="row"
        style={{
          width: '100%',
          justifyContent: 'space-between',
          gap: 8,
          background: 'var(--bg)',
          color: selected ? 'var(--text)' : 'var(--text-dim)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '10px 12px',
          fontSize: 15,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          textAlign: 'left',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : emptyLabel}
        </span>
        <span aria-hidden style={{ opacity: 0.6, fontSize: 12 }}>▾</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 20,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: 'var(--bg-panel)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
            overflow: 'hidden',
          }}
        >
          <input
            ref={inputRef}
            value={query}
            placeholder={placeholder}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((i) => Math.min(i + 1, matches.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((i) => Math.max(i - 1, 0));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                const hit = matches[active];
                if (hit) pick(hit.value);
              } else if (e.key === 'Escape') {
                setOpen(false);
                setQuery('');
              }
            }}
            style={{
              width: '100%',
              border: 'none',
              borderBottom: '1px solid var(--border)',
              borderRadius: 0,
              background: 'var(--bg-panel-2)',
            }}
          />
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {allowEmpty && !query && (
              <Option label={emptyLabel} dim onClick={() => pick(null)} />
            )}
            {matches.length === 0 ? (
              <div className="muted" style={{ padding: '12px 14px', fontSize: 13 }}>
                Nothing matches “{query}”.
              </div>
            ) : (
              matches.map((o, i) => (
                <Option
                  key={o.value}
                  label={o.label}
                  hint={o.hint}
                  highlighted={i === active}
                  selected={o.value === value}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(o.value)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Option({
  label,
  hint,
  highlighted,
  selected,
  dim,
  onClick,
  onMouseEnter,
}: {
  label: string;
  hint?: string;
  highlighted?: boolean;
  selected?: boolean;
  dim?: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
}) {
  return (
    <div
      role="option"
      aria-selected={selected}
      onMouseEnter={onMouseEnter}
      onMouseDown={(e) => {
        // mousedown, not click: the outside-click handler closes the panel on
        // mousedown, and a click listener would never fire.
        e.preventDefault();
        onClick();
      }}
      className="row"
      style={{
        justifyContent: 'space-between',
        gap: 10,
        padding: '9px 14px',
        cursor: 'pointer',
        fontSize: 14,
        background: highlighted ? 'var(--bg-panel-2)' : 'transparent',
        color: dim ? 'var(--text-dim)' : 'var(--text)',
        fontWeight: selected ? 700 : 400,
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {hint && (
        <span className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 12, flex: 'none' }}>
          {hint}
        </span>
      )}
    </div>
  );
}
